import { useEffect, useMemo, useState } from "react";
import {
  Alert,
  App as AntdApp,
  Button,
  Input,
  Modal,
  Select,
  Space,
  Switch,
  Table,
  Tag,
  Tooltip,
  Typography,
} from "antd";
import {
  DeleteOutlined,
  PlusOutlined,
  ExclamationCircleOutlined,
  CheckCircleOutlined,
} from "@ant-design/icons";
import { useTranslation } from "react-i18next";

import { apiClient } from "../../../services/api/apiClient";
import type { AuditPlot } from "../../../types/audit";

const { Text } = Typography;

// The editor only reads `id` and `name` from each control point. We
// keep this minimal local type to avoid pulling the full
// ControlPoint shape in here (which lives across two files in the
// codebase and would force a wider import).
interface ControlPointOpt {
  id: string;
  name: string;
}

interface RowDraft {
  /** Stable per-row id used only for React keys and error indexing. */
  key: string;
  lab_code: string;
  sample_type: "leaf" | "skin";
  waypoint: string;
  is_control_point: boolean;
  // AuditPlot.id is typed as a string in `types/audit` (even though
  // the DB column is a BigAutoField — Django accepts the string lookup
  // either way).
  audit_plot?: string;
  control_point?: string;
}

interface BulkResponseRow {
  index: number;
  id: string;
  lab_code: string;
  waypoint: string | null;
  geo_resolved: boolean;
}

interface BulkResponse {
  created: number;
  results: BulkResponseRow[];
}

interface BulkErrorResponse {
  detail?: string;
  row_errors?: Record<number, Record<string, string>>;
}

interface SampleBatchEditorProps {
  visible: boolean;
  auditId: number;
  auditPlots: AuditPlot[];
  controlPoints: ControlPointOpt[];
  loadingPlots?: boolean;
  loadingControlPoints?: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

/** lab_code: 5 chars, uppercase letters/digits, can't start with O. */
const LAB_CODE_RE = /^[A-NP-Z0-9][A-Z0-9]{4}$/;

const emptyRow = (): RowDraft => ({
  key: crypto.randomUUID(),
  lab_code: "",
  sample_type: "leaf",
  waypoint: "",
  is_control_point: false,
  audit_plot: undefined,
  control_point: undefined,
});

/**
 * Spreadsheet-style editor for creating many samples in one go.
 *
 * Each row is validated as the user types (regex + intra-batch
 * duplicates). On submit the whole batch goes to `POST /samples/bulk/`
 * in one transaction; if the backend reports per-row errors (typically
 * `lab_code` colliding with another sample already in the DB), they
 * land back on the matching cells. On success the modal closes and
 * the parent refetches the audit data so the new samples show up on
 * the table + map (waypoint-matched ones get a flag).
 */
export const SampleBatchEditor: React.FC<SampleBatchEditorProps> = ({
  visible,
  auditId,
  auditPlots,
  controlPoints,
  loadingPlots,
  loadingControlPoints,
  onClose,
  onSuccess,
}) => {
  const { t } = useTranslation();
  const { message } = AntdApp.useApp();
  const [rows, setRows] = useState<RowDraft[]>([emptyRow(), emptyRow(), emptyRow()]);
  const [serverErrors, setServerErrors] = useState<
    Record<number, Record<string, string>>
  >({});
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (visible) {
      setRows([emptyRow(), emptyRow(), emptyRow()]);
      setServerErrors({});
    }
  }, [visible]);

  // ----- per-row validation (client) --------------------------------

  const localErrors = useMemo(() => {
    const out: Record<number, Record<string, string>> = {};
    const seenLab = new Map<string, number>();
    const seenWpt = new Map<string, number>();

    rows.forEach((row, idx) => {
      const errs: Record<string, string> = {};
      const lab = row.lab_code.trim();
      if (!lab) {
        errs.lab_code = t(
          "sample.batch.err_lab_required",
          "Obligatorio",
        );
      } else if (!LAB_CODE_RE.test(lab)) {
        errs.lab_code = t(
          "sample.batch.err_lab_format",
          "5 chars, mayús/dígitos, no «O» inicial",
        );
      } else if (seenLab.has(lab)) {
        errs.lab_code = t(
          "sample.batch.err_lab_dup",
          "Duplicado (fila {{n}})",
          { n: (seenLab.get(lab) ?? 0) + 1 },
        );
      } else {
        seenLab.set(lab, idx);
      }

      const wpt = row.waypoint.trim();
      if (wpt) {
        if (seenWpt.has(wpt)) {
          errs.waypoint = t(
            "sample.batch.err_wpt_dup",
            "Duplicado (fila {{n}})",
            { n: (seenWpt.get(wpt) ?? 0) + 1 },
          );
        } else {
          seenWpt.set(wpt, idx);
        }
      }

      if (row.is_control_point) {
        if (!row.control_point) {
          errs.control_point = t(
            "sample.batch.err_cp_required",
            "Selecciona punto de control",
          );
        }
      } else {
        if (!row.audit_plot) {
          errs.audit_plot = t(
            "sample.batch.err_plot_required",
            "Selecciona parcela",
          );
        }
      }

      if (Object.keys(errs).length) out[idx] = errs;
    });
    return out;
  }, [rows, t]);

  /** Combined view: live client errors + last server-rejection errors. */
  const allErrors = useMemo(() => {
    const out: Record<number, Record<string, string>> = { ...localErrors };
    for (const [k, errs] of Object.entries(serverErrors)) {
      const idx = Number(k);
      out[idx] = { ...(out[idx] ?? {}), ...errs };
    }
    return out;
  }, [localErrors, serverErrors]);

  const hasErrors = Object.keys(allErrors).length > 0;

  // ----- editing helpers --------------------------------------------

  const updateRow = (idx: number, patch: Partial<RowDraft>) => {
    setRows((prev) => {
      const next = [...prev];
      next[idx] = { ...next[idx], ...patch };
      return next;
    });
    // Clear server error on the field the user just edited so the
    // red border goes away as they fix it.
    setServerErrors((prev) => {
      if (!prev[idx]) return prev;
      const fields = Object.keys(patch);
      const remaining = { ...prev[idx] };
      let touched = false;
      for (const f of fields) {
        if (remaining[f]) {
          delete remaining[f];
          touched = true;
        }
      }
      if (!touched) return prev;
      const next = { ...prev };
      if (Object.keys(remaining).length === 0) delete next[idx];
      else next[idx] = remaining;
      return next;
    });
  };

  const addRow = () => setRows((prev) => [...prev, emptyRow()]);
  const removeRow = (idx: number) =>
    setRows((prev) => prev.filter((_, i) => i !== idx));

  // ----- submit -----------------------------------------------------

  const handleSubmit = async () => {
    if (hasErrors) {
      message.error(
        t(
          "sample.batch.fix_errors",
          "Hay filas con errores — revisa los campos en rojo.",
        ),
      );
      return;
    }
    setSubmitting(true);
    try {
      const payload = {
        audit: auditId,
        samples: rows.map((r) => ({
          lab_code: r.lab_code.trim(),
          sample_type: r.sample_type,
          waypoint: r.waypoint.trim() || undefined,
          is_control_point: r.is_control_point,
          audit_plot: r.is_control_point ? undefined : r.audit_plot,
          control_point: r.is_control_point ? r.control_point : undefined,
        })),
      };
      const res = await apiClient.post<BulkResponse>(
        "/samples/bulk/",
        payload,
      );
      const matched = res.data.results.filter((r) => r.geo_resolved).length;
      message.success(
        t(
          "sample.batch.success",
          "Creadas {{n}} muestras · {{matched}} con waypoint resuelto",
          { n: res.data.created, matched },
        ),
      );
      setServerErrors({});
      onSuccess();
      onClose();
    } catch (err: any) {
      const data: BulkErrorResponse | undefined =
        err?.originalError?.response?.data ?? err?.response?.data;
      if (data?.row_errors) {
        setServerErrors(data.row_errors);
        message.error(
          data.detail ||
            t(
              "sample.batch.server_reject",
              "El servidor ha rechazado algunas filas — revisa los errores marcados.",
            ),
        );
      } else {
        message.error(
          data?.detail ||
            err?.message ||
            t("sample.batch.unknown_error", "No se pudo crear el lote"),
        );
      }
    } finally {
      setSubmitting(false);
    }
  };

  // ----- columns ----------------------------------------------------

  const cellError = (idx: number, field: string): string | undefined =>
    allErrors[idx]?.[field];

  const errorBorder = (idx: number, field: string): React.CSSProperties =>
    cellError(idx, field) ? { borderColor: "#ff4d4f" } : {};

  const columns = [
    {
      title: "#",
      key: "rowNum",
      width: 40,
      render: (_: any, __: RowDraft, idx: number) => (
        <Text type="secondary">{idx + 1}</Text>
      ),
    },
    {
      title: t("sample.batch.col_lab_code", "Cód. Lab."),
      key: "lab_code",
      width: 130,
      render: (_: any, row: RowDraft, idx: number) => (
        <Tooltip title={cellError(idx, "lab_code")} open={!!cellError(idx, "lab_code") && undefined}>
          <Input
            size="small"
            value={row.lab_code}
            maxLength={5}
            placeholder="AB123"
            style={{
              textTransform: "uppercase",
              fontFamily: "monospace",
              ...errorBorder(idx, "lab_code"),
            }}
            onChange={(e) =>
              updateRow(idx, {
                lab_code: e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, ""),
              })
            }
            status={cellError(idx, "lab_code") ? "error" : undefined}
          />
        </Tooltip>
      ),
    },
    {
      title: t("sample.batch.col_type", "Tipo"),
      key: "sample_type",
      width: 95,
      render: (_: any, row: RowDraft, idx: number) => (
        <Select
          size="small"
          style={{ width: "100%" }}
          value={row.sample_type}
          onChange={(v) => updateRow(idx, { sample_type: v })}
          options={[
            { value: "leaf", label: t("sample.type_leaf", "Hoja") },
            { value: "skin", label: t("sample.type_skin", "Piel") },
          ]}
        />
      ),
    },
    {
      title: t("sample.batch.col_waypoint", "Waypoint"),
      key: "waypoint",
      width: 130,
      render: (_: any, row: RowDraft, idx: number) => (
        <Tooltip title={cellError(idx, "waypoint")}>
          <Input
            size="small"
            value={row.waypoint}
            placeholder="WP01"
            style={errorBorder(idx, "waypoint")}
            onChange={(e) => updateRow(idx, { waypoint: e.target.value })}
            status={cellError(idx, "waypoint") ? "error" : undefined}
          />
        </Tooltip>
      ),
    },
    {
      title: t("sample.batch.col_is_cp", "¿PC?"),
      key: "is_control_point",
      width: 70,
      align: "center" as const,
      render: (_: any, row: RowDraft, idx: number) => (
        <Switch
          size="small"
          checked={row.is_control_point}
          onChange={(checked) =>
            updateRow(idx, {
              is_control_point: checked,
              audit_plot: checked ? undefined : row.audit_plot,
              control_point: checked ? row.control_point : undefined,
            })
          }
        />
      ),
    },
    {
      title: t("sample.batch.col_target", "Parcela / PC"),
      key: "target",
      render: (_: any, row: RowDraft, idx: number) => {
        if (row.is_control_point) {
          return (
            <Tooltip title={cellError(idx, "control_point")}>
              <Select
                size="small"
                style={{ width: "100%" }}
                value={row.control_point}
                showSearch
                allowClear
                placeholder={t("sample.batch.pick_cp", "Punto de control")}
                loading={loadingControlPoints}
                options={controlPoints.map((cp) => ({
                  value: cp.id,
                  label: cp.name,
                }))}
                filterOption={(input, option) =>
                  (option?.label ?? "")
                    .toString()
                    .toLowerCase()
                    .includes(input.toLowerCase())
                }
                status={cellError(idx, "control_point") ? "error" : undefined}
                onChange={(v) => updateRow(idx, { control_point: v })}
              />
            </Tooltip>
          );
        }
        return (
          <Tooltip title={cellError(idx, "audit_plot")}>
            <Select
              size="small"
              style={{ width: "100%" }}
              value={row.audit_plot}
              showSearch
              allowClear
              placeholder={t("sample.batch.pick_plot", "Parcela")}
              loading={loadingPlots}
              options={auditPlots.map((p) => ({
                value: p.id,
                label: p.name,
              }))}
              filterOption={(input, option) =>
                (option?.label ?? "")
                  .toString()
                  .toLowerCase()
                  .includes(input.toLowerCase())
              }
              status={cellError(idx, "audit_plot") ? "error" : undefined}
              onChange={(v) => updateRow(idx, { audit_plot: v })}
            />
          </Tooltip>
        );
      },
    },
    {
      title: "",
      key: "actions",
      width: 40,
      render: (_: any, __: RowDraft, idx: number) => (
        <Button
          type="text"
          danger
          size="small"
          icon={<DeleteOutlined />}
          disabled={rows.length === 1}
          onClick={() => removeRow(idx)}
        />
      ),
    },
  ];

  // Aggregate stats for the header banner.
  const stats = useMemo(() => {
    const errored = Object.keys(allErrors).length;
    return { total: rows.length, errored, ok: rows.length - errored };
  }, [rows.length, allErrors]);

  return (
    <Modal
      title={t("sample.batch.title", "Crear muestras en lote")}
      open={visible}
      onCancel={submitting ? undefined : onClose}
      onOk={handleSubmit}
      okText={t("sample.batch.create_all", "Crear todas")}
      cancelText={t("common.cancel", "Cancelar")}
      confirmLoading={submitting}
      width={920}
      okButtonProps={{ disabled: hasErrors || submitting }}
      destroyOnHidden
    >
      <Alert
        type={hasErrors ? "warning" : "info"}
        showIcon
        icon={
          hasErrors ? <ExclamationCircleOutlined /> : <CheckCircleOutlined />
        }
        style={{ marginBottom: 12 }}
        message={
          <Space size={12}>
            <span>
              <Tag color="default">{stats.total}</Tag>{" "}
              {t("sample.batch.rows", "filas")}
            </span>
            {stats.errored > 0 ? (
              <span>
                <Tag color="red">{stats.errored}</Tag>{" "}
                {t("sample.batch.with_errors", "con errores")}
              </span>
            ) : (
              <span>
                {t(
                  "sample.batch.ready",
                  "Lote válido — al pulsar «Crear todas» se intentan guardar en una sola transacción.",
                )}
              </span>
            )}
          </Space>
        }
        description={t(
          "sample.batch.hint",
          "Reglas: código de laboratorio de 5 caracteres en mayúsculas, sin empezar por «O», únicos en el lote y en la base de datos. Waypoints duplicados dentro del lote también se marcan. Si la muestra tiene un waypoint que coincide con el GPX del día se asocia su geometría automáticamente.",
        )}
      />

      <Table<RowDraft>
        dataSource={rows}
        columns={columns}
        rowKey="key"
        size="small"
        pagination={false}
        scroll={{ x: 800, y: 420 }}
      />

      <div style={{ marginTop: 12, display: "flex", justifyContent: "space-between" }}>
        <Button icon={<PlusOutlined />} onClick={addRow}>
          {t("sample.batch.add_row", "Añadir fila")}
        </Button>
        <Text type="secondary" style={{ fontSize: 12 }}>
          {t(
            "sample.batch.tip",
            "Sugerencia: pulsa Tab para saltar entre celdas. Los códigos se convierten a mayúsculas automáticamente.",
          )}
        </Text>
      </div>
    </Modal>
  );
};

export default SampleBatchEditor;
